import type { WelcomeDiscount } from "@cleanbudget/contract/newsletter";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent } from "@cleanbudget/ui/components/card";
import { IconAlertCircle, IconDownload, IconLoader2 } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { HelpButton } from "@/components/help/help-button";
import { PageHeader } from "@/components/page-header";
import { client, orpc } from "@/lib/orpc";
import { WelcomeSettings } from "./-components/welcome-settings";

const countQuery = () => orpc.newsletter.count.queryOptions();
const settingsQuery = () => orpc.newsletter.settings.queryOptions();

/** Nieuwsbrief: how many addresses are signed up, the CSV export of them, and the welcome code. */
export const Route = createFileRoute("/_admin/content/newsletter/")({
	loader: ({ context }) =>
		Promise.all([
			context.queryClient.ensureQueryData(countQuery()),
			context.queryClient.ensureQueryData(settingsQuery()),
		]),
	component: NewsletterPage,
});

const number = new Intl.NumberFormat("nl-NL");

function NewsletterPage() {
	const { t } = useTranslation("newsletter");
	const { t: tContent } = useTranslation("content");
	const { data } = useSuspenseQuery(countQuery());
	const settings = useSuspenseQuery(settingsQuery());
	const queryClient = useQueryClient();
	const save = useMutation({
		...orpc.newsletter.updateSettings.mutationOptions(),
		onSuccess: (saved) => queryClient.setQueryData(settingsQuery().queryKey, saved),
	});
	const [formKey, setFormKey] = useState(0);
	const reload = async () => {
		await settings.refetch();
		setFormKey((key) => key + 1);
	};
	const download = useMutation({
		mutationFn: async () => saveFile(await client.newsletter.export()),
	});

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/content">{tContent("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
				help="newsletter:newsletter"
			/>
			<div className="grid gap-4 sm:grid-cols-[minmax(0,14rem)_1fr]">
				<Card>
					<CardContent className="flex flex-col gap-1">
						<div className="flex items-center gap-1">
							<h2 className="text-sm text-muted-foreground">{t("count.label")}</h2>
							<HelpButton topic="newsletter:signups" />
						</div>
						<p className="font-heading text-3xl font-semibold tabular-nums">{number.format(data.active)}</p>
						<p className="text-xs text-muted-foreground">{t("count.help")}</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="flex flex-col items-start gap-3">
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1.5">
								<h2 className="font-heading text-base font-medium">{t("export.title")}</h2>
								<HelpButton topic="newsletter:export" />
							</div>
							<p className="text-sm text-muted-foreground">{t("export.description")}</p>
						</div>
						{download.isError ? (
							<Alert variant="destructive">
								<IconAlertCircle aria-hidden />
								<AlertDescription>{t("export.failed")}</AlertDescription>
							</Alert>
						) : null}
						<Button onClick={() => download.mutate()} disabled={download.isPending}>
							{download.isPending ? (
								<IconLoader2 className="animate-spin" aria-hidden />
							) : (
								<IconDownload aria-hidden />
							)}
							{download.isPending ? t("export.busy") : t("export.button")}
						</Button>
					</CardContent>
				</Card>
			</div>
			<WelcomeSettings
				key={formKey}
				settings={settings.data}
				onSave={(welcome: WelcomeDiscount) => save.mutateAsync({ welcome, version: settings.data.version })}
				onReload={reload}
			/>
		</div>
	);
}

/** Hands a file from the API to the browser as a download, under the name the API gave it. */
function saveFile(file: Blob) {
	const url = URL.createObjectURL(file);
	const link = document.createElement("a");
	link.href = url;
	link.download = file instanceof File && file.name ? file.name : "nieuwsbrief.csv"; // i18n-ignore: a file name
	document.body.append(link);
	link.click();
	link.remove();
	setTimeout(() => URL.revokeObjectURL(url), 1000);
}
